import {
  type FC,
  type FormEvent,
  type KeyboardEvent,
  MouseEventHandler,
  useRef,
  useState,
} from "react";
import cx from "classnames";

import styles from "./ChatInput.module.css";
import { Button } from "../Button";
import { UnstyledButton } from "../UnstyledButton";
import CrossIcon from "./assets/cross.svg?react";
import ArrowUpIcon from "./assets/arrow-up.svg?react";
import EqualizerIcon from "./assets/equalizer.svg?react";
import StopIcon from "./assets/stop.svg?react";
import { useAudioRecording } from "../../hooks/useAudioRecording";
import { useViewportSize } from "../../context/ViewportContext";
import { ButtonSpinner } from "../ButtonSpinner";
import { useI18n } from "../../i18n";

type Props = {
  status: "idle" | "sending" | "awaiting" | "rendering" | "error";
  placeholder?: string;
  disabled?: boolean;
  isSpeaking?: boolean;
  onSubmit: (message: string) => void;
  onSendVoiceMessage?: (blob: Blob, mimeType: string) => Promise<void> | void;
  onStopSpeaking?: () => void;
};

/**
 * Inline margin for audio wave visualization
 * 
 * 4 - hud margin
 * 10 - hud padding
 * 36 - button width
 * 8 - gap in chat input
 */
const MARGIN_INLINE = 2 * (4 + 10 + 36 + 8);

export const ChatInput: FC<Props> = ({
  status,
  placeholder,
  disabled,
  isSpeaking,
  onSubmit,
  onSendVoiceMessage,
  onStopSpeaking,
}) => {
  const i18n = useI18n();
  const { width } = useViewportSize();
  const [value, setValue] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    status: audioStatus,
    isRecording,
    toggle,
    cancel
  } = useAudioRecording({
    canvasRef,
    onReady: async (blob, mimeType) => {
      if (onSendVoiceMessage) {
        await onSendVoiceMessage(blob, mimeType);
      }
    },
  });

  const trimmedValue = value.trim();
  const canSubmitText = trimmedValue.length > 0 && status === "idle";
  const canStopSpeaking = Boolean(isSpeaking && onStopSpeaking);
  const isButtonDisabled =
    !canStopSpeaking &&
    (disabled ||
      audioStatus === "sending" ||
      (!isRecording && status !== "idle"));
  const isInputDisabled =
    disabled || status !== "idle" || isRecording || audioStatus === "sending";

  const handlePrimaryAction: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    if (isButtonDisabled) {
      return;
    }

    if (canStopSpeaking) {
      onStopSpeaking?.();
      return;
    }
    
    if (canSubmitText) {
      onSubmit(trimmedValue);
      setValue("");
      return;
    }
    
    toggle();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (canSubmitText) {
      onSubmit(trimmedValue);
      setValue("");
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSubmitText) {
        onSubmit(trimmedValue);
        setValue("");
      }
    }
  };

  return (
    <form className={cx(styles.chatInput, isRecording && styles.recording)} onSubmit={handleSubmit}>
      <div className={styles.textareaWrapper}>
        <input
          type="text"
          name="message"
          className={cx(styles.textarea)}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isInputDisabled}
        />
        <div
          className={cx(styles.visualizer)}
          aria-hidden={!isRecording}
        >
          <Button onClick={cancel}>
            <StopIcon />
          </Button>
          <canvas
            ref={canvasRef}
            width={2 * (width - MARGIN_INLINE)}
            height={56}
          />
        </div>
        {!isRecording && value.length > 0 ? (
          <UnstyledButton
            className={styles.clear}
            onClick={() => setValue("")}
            aria-label={i18n.chatInput.clearMessage}
          >
            <CrossIcon />
          </UnstyledButton>
        ) : null}
      </div>
      
      <Button
        type="submit"
        appearance="primary"
        disabled={isButtonDisabled}
        className={styles.submit}
        onClick={handlePrimaryAction}
      >
        {canStopSpeaking ? (
          <StopIcon />
        ) : status === "sending" ? (
          <ButtonSpinner />
        ) : isRecording || trimmedValue.length > 0 ? (
          <ArrowUpIcon />
        ) : (
          <EqualizerIcon />
        )}
      </Button>
    </form>
  );
};
